import { success } from "App/popupMessage";
import { getTaskResultLimitByDocumentId } from "Areas/Documents/documentDetails/redux/selectors";
import { NODE_SERVER_IGNORED_FIELDS } from "Areas/SharedFeatures/types/nodeSource";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import omit from "lodash/omit";
import locale from "./actionCreators.locale";
import actionTypes from "./actionTypes";
import api from "./api";
import { getMethods, getObjectsIgnoreOutdated, getObjectsInProgress, getObjectsOutdated, getObjectsUsedByUI } from "./selectors";
export default {
    setIsExpanded: (isExpanded) => ({
        type: actionTypes.SET_IS_EXPANDED,
        isExpanded
    }),
    setNavigateTo: (navigateToId) => ({
        type: actionTypes.SET_NAVIGATE_TO,
        navigateToId
    }),
    toggleMethodSelected: (method, nodesSource, selected) => ({
        type: actionTypes.TOGGLE_METHOD_SELECTED,
        method,
        nodesSource,
        selected
    }),
    clearMethodsSelected: () => ({
        type: actionTypes.CLEAR_METHODS_SELECTED
    }),
    setTab: (tab) => ({
        type: actionTypes.SET_TAB,
        tab
    }),
    setContentModeTab: (contentModeTab) => ({
        type: actionTypes.SET_CONTENT_MODE_TAB,
        contentModeTab
    }),
    getDocuments: (projectId) => ({
        type: actionTypes.GET_DOCUMENTS,
        payload: {
            promise: () => api.getDocuments(projectId)
        },
        meta: { projectId },
        scope: `${actionTypes.GET_DOCUMENTS}_${projectId}`
    }),
    setObjectsOutdated: () => ({
        type: actionTypes.SET_OBJECTS_OUTDATED
    }),
    getObjects: (skipIfInProgress = true, skipIfNoData = false) => (dispatch, getState) => {
        const state = getState();
        if (skipIfInProgress && getObjectsInProgress(state))
            return dispatch({
                type: actionTypes.SET_OBJECTS_USED_BY_UI
            });
        if ((skipIfNoData && !getObjectsUsedByUI(state)) ||
            (getObjectsIgnoreOutdated(state) && !getObjectsOutdated(state)))
            return undefined;
        return dispatch({
            type: actionTypes.GET_OBJECTS,
            payload: {
                promise: () => api.getObjects()
            }
        });
    },
    getMethods: () => (dispatch, getState) => isEmpty(getMethods(getState())) &&
        dispatch({
            type: actionTypes.GET_METHODS,
            payload: {
                promise: () => api.getMethods()
            }
        }),
    setDocumentSelected: (documentId, selected) => ({
        type: actionTypes.SET_DOCUMENT_SELECTED,
        documentId,
        selected
    }),
    setDocumentType: (documentId, documentType) => ({
        type: actionTypes.SET_DOCUMENT_TYPE,
        documentId,
        documentType
    }),
    runMethods: (documentId, data) => {
        const request = compact(map(data, d => !isEmpty(d.nodesSource)
            ? {
                methodId: d.method.Id,
                nodesSource: map(d.nodesSource, s => omit(s, NODE_SERVER_IGNORED_FIELDS))
            }
            : null));
        if (isEmpty(request))
            return undefined;
        return (dispatch, getState) => {
            const taskResultLimit = getTaskResultLimitByDocumentId(getState(), documentId);
            dispatch({
                type: actionTypes.RUN_METHODS,
                payload: {
                    promise: () => api.runMethods(documentId, request, taskResultLimit)
                },
                meta: { documentId },
                completed: (d) => {
                    d(success(request.length > 1 ? locale.METHODS_RUN_SUCCESSFULLY : locale.METHOD_RUN_SUCCESSFULLY));
                },
                scope: `${actionTypes.RUN_METHODS}_${documentId}`
            });
        };
    },
    setShowScriptEditorPanel: (show, data, targetSelector, targetOffset) => ({
        type: actionTypes.SET_SHOW_SCRIPT_EDITOR_PANEL,
        data,
        show,
        targetSelector,
        targetOffset
    }),
    saveScript: (data) => ({
        type: actionTypes.SAVE_SCRIPT,
        payload: {
            promise: () => api.saveScript(data)
        },
        meta: {
            data
        },
        scope: `${actionTypes.SAVE_SCRIPT}_${data.Id}`
    }),
    setScriptDraft: (draft) => ({
        type: actionTypes.SET_SCRIPT_DRAFT,
        draft
    }),
    getScripts: () => ({
        type: actionTypes.GET_SCRIPTS,
        payload: {
            promise: () => api.getScripts()
        }
    }),
    runScript: (documentId, scriptId, nodesSource) => (dispatch, getState) => {
        const taskResultLimit = getTaskResultLimitByDocumentId(getState(), documentId);
        return dispatch({
            type: actionTypes.RUN_SCRIPT,
            payload: {
                promise: () => api.runScript(documentId, scriptId, map(nodesSource, s => omit(s, NODE_SERVER_IGNORED_FIELDS)), taskResultLimit)
            },
            meta: { documentId },
            completed: (d) => {
                d(success(locale.SCRIPT_RUN_SUCCESSFULLY));
            },
            scope: `${actionTypes.RUN_SCRIPT}_${documentId}_${scriptId}`
        });
    },
    deleteScript: (scriptId) => ({
        type: actionTypes.DELETE_SCRIPT,
        payload: {
            promise: () => api.deleteScript(scriptId)
        },
        meta: { scriptId },
        scope: `${actionTypes.DELETE_SCRIPT}_${scriptId}`
    }),
    getTasks: (documentId) => ({
        type: actionTypes.GET_TASKS,
        payload: {
            promise: () => documentId && api.getDocumentTasks(documentId)
        },
        meta: {
            documentId
        },
        scope: `${actionTypes.GET_TASKS}_${documentId}`
    }),
    setRequestTaskOutdated: (requestTaskId, documentId) => ({
        type: actionTypes.SET_REQUEST_TASK_OUTDATED,
        requestTaskId,
        documentId
    }),
    getRequestTask: (requestTaskId) => ({
        type: actionTypes.GET_REQUEST_TASK,
        payload: {
            promise: () => api.getRequestTask(requestTaskId)
        },
        meta: {
            requestTaskId
        },
        scope: `${actionTypes.GET_REQUEST_TASK}_${requestTaskId}`
    }),
    stopRequestTask: (requestTaskId) => ({
        type: actionTypes.STOP_REQUEST_TASK,
        payload: {
            promise: () => api.stopRequestTask(requestTaskId)
        },
        meta: {
            requestTaskId
        },
        scope: `${actionTypes.STOP_REQUEST_TASK}_${requestTaskId}`
    }),
    restartRequestTask: (requestTaskId) => ({
        type: actionTypes.RESTART_REQUEST_TASK,
        payload: {
            promise: () => api.restartRequestTask(requestTaskId)
        },
        meta: {
            requestTaskId
        },
        scope: `${actionTypes.RESTART_REQUEST_TASK}_${requestTaskId}`
    }),
    setSourceTaskIdsToHighlight: (sourceTaskIds, documentId) => ({
        type: actionTypes.SET_SOURCE_TASK_IDS_TO_HIGHLIGHT,
        sourceTaskIds,
        documentId
    }),
    clearSourceTaskIdsToHighlight: (documentId) => ({
        type: actionTypes.CLEAR_SOURCE_TASK_IDS_TO_HIGHLIGHT,
        documentId
    })
};
